// Story 6.22 (ADO-1201) — Admin auth-audit-log screen (AC3/AC5).
//
// Renders the auth-audit log served by `GET /admin/audit-log` as a
// read-only forensic/compliance surface (FR11), using the Story 6.9 design
// system and mirroring the Story 6.10/6.11 screen conventions. Records list in
// stable most-recent-first order matching the server's `(occurred_at desc, id
// desc)` total order; pagination is cursor-based ("Load more" passes the last
// page's exclusive `(occurredAt, id)` cursor), so pages never skip or duplicate
// rows sharing a millisecond (AC3/AC4). Filters by subject + time window.
//
// Server remains authoritative (NFR18): `session.role` gates DISPLAY only — the
// endpoint re-checks via `@Roles('SystemAdministrator')`. The log is
// READ-ONLY here (no mutation affordance — Story 6.8 immutability preserved).
// a11y via the DS components (NFR24–26); interaction stays ≤3s (NFR6).
//
// Enabler E6-A (ADO-2266) AC22, OP-4 option B: logins and logouts are written only to the
// platform's logs (CloudWatch), never to the database, so the endpoint returns no
// authentication events. The screen says so in a notice and in the empty-table message.

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  AppShell,
  Banner,
  Button,
  Card,
  FormField,
  Table,
  type TableColumn,
} from '../../design-system';
import { isSystemAdministrator } from '../../auth/rbac';
import { listAuditLog, type AuditLogCursor, type AuditLogEntry } from '../../api/audit-log.client';
import styles from './AuditLog.module.css';

// Default page size the screen requests (bounded server-side; never widens the
// Story 6.8 seam ceiling). A "Load more" affordance pages with the cursor.
const PAGE_SIZE = 50;

export interface AuditLogProps {
  /**
   * Non-authoritative session values (from ID-token claims / `useSessionStore`).
   * `role` drives the client-side display gate (AC5) — the server re-checks.
   */
  session?: { role?: string | null };
}

interface AppliedFilters {
  readonly sub: string;
  readonly from: string;
  readonly to: string;
}

const EMPTY_FILTERS: AppliedFilters = { sub: '', from: '', to: '' };

export function AuditLog({ session }: AuditLogProps) {
  // Client-side display gate (AC5). The route already blocks non-admins; this
  // keeps the guard explicit at the component boundary too. Server authoritative.
  const canView = isSystemAdministrator(session?.role);

  const [entries, setEntries] = useState<AuditLogEntry[]>([]);
  const [nextCursor, setNextCursor] = useState<AuditLogCursor | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadError, setLoadError] = useState(false);

  // Draft (input) vs. applied filters: the query only re-runs on "Apply", so
  // typing does not fire a request per keystroke.
  const [draftSub, setDraftSub] = useState('');
  const [draftFrom, setDraftFrom] = useState('');
  const [draftTo, setDraftTo] = useState('');
  const [applied, setApplied] = useState<AppliedFilters>(EMPTY_FILTERS);

  // Guard setState against an unmount mid-fetch (route gate redirect / filter
  // change while a page is in flight).
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Monotonic request id: each load() bumps it and only the latest request may
  // commit. Guards against an out-of-order response — e.g. a "Load more" in
  // flight when the user re-applies filters — appending stale rows or clobbering
  // the cursor.
  const requestSeqRef = useRef(0);

  // Load a page. `cursor === null` = fresh page 1 (replaces the list); a cursor
  // appends the next page (AC3 cursor pagination, matching the index order).
  const load = useCallback(async (filters: AppliedFilters, cursor: AuditLogCursor | null) => {
    const fresh = cursor === null;
    const seq = ++requestSeqRef.current;
    // Still mounted AND still the latest request — a superseded response must
    // not commit.
    const current = () => mountedRef.current && requestSeqRef.current === seq;
    if (fresh) {
      setLoading(true);
      setLoadError(false);
    } else {
      setLoadingMore(true);
    }
    try {
      const page = await listAuditLog({
        sub: filters.sub,
        from: filters.from,
        to: filters.to,
        limit: PAGE_SIZE,
        cursor,
      });
      if (!current()) return;
      setEntries((prev) => (fresh ? page.entries : [...prev, ...page.entries]));
      setNextCursor(page.nextCursor);
      setLoadError(false);
    } catch {
      if (!current()) return;
      // A failed "Load more" is still a load error for the surface; a failed
      // page-1 shows the error banner in place of the table.
      setLoadError(true);
    } finally {
      // No `return` in a finally (no-unsafe-finally) — guard the setState, and
      // only the latest request may clear the loading flags.
      if (current()) {
        setLoading(false);
        setLoadingMore(false);
      }
    }
  }, []);

  // Initial page 1 (admins only — no fetch fires for a non-admin display gate).
  useEffect(() => {
    if (!canView) {
      setLoading(false);
      return;
    }
    void load(EMPTY_FILTERS, null);
  }, [canView, load]);

  const applyFilters = useCallback(() => {
    // The datetime-local inputs are zone-less wall-clock; normalise each to a UTC
    // ISO instant so the server's window matches the UTC-rendered column (project
    // rule: ISO-8601 UTC on the wire, never server-local).
    const next: AppliedFilters = {
      sub: draftSub,
      from: toUtcInstant(draftFrom),
      to: toUtcInstant(draftTo),
    };
    setApplied(next);
    setEntries([]);
    setNextCursor(null);
    void load(next, null);
  }, [draftSub, draftFrom, draftTo, load]);

  const loadMore = useCallback(() => {
    if (nextCursor === null) return;
    void load(applied, nextCursor);
  }, [applied, nextCursor, load]);

  const columns: TableColumn<AuditLogEntry>[] = [
    { key: 'occurredAt', header: 'Occurred at', render: (e) => e.occurredAt },
    { key: 'kind', header: 'Event', render: (e) => e.kind },
    { key: 'sub', header: 'Subject', render: (e) => e.sub },
    { key: 'actor', header: 'Actor', render: (e) => e.actor },
    {
      key: 'details',
      header: 'Details',
      render: (e) => <PayloadDetails payload={e.payload} />,
    },
  ];

  if (!canView) {
    return (
      <AppShell title="Authentication audit log" subtitle="Read-only authentication event trail.">
        <Banner type="error" title="Access restricted">
          You don't have permission to view the authentication audit log.
        </Banner>
      </AppShell>
    );
  }

  return (
    <AppShell
      title="Authentication audit log"
      subtitle="A read-only, most-recent-first trail of authentication events for your organisation."
    >
      <Banner type="info" title="Login events are not stored here">
        Logins and logouts are recorded in the platform's system logs, not in ShopLink, so they
        don't appear in this list.
      </Banner>
      {loadError && entries.length === 0 ? (
        <Banner type="error" title="Couldn't load the audit log">
          Please reload the page and try again.
        </Banner>
      ) : (
        <>
          {loadError ? (
            <Banner type="warning" title="Couldn't load more">
              The next page couldn't be loaded. Please try again.
            </Banner>
          ) : null}
          <Card
            title="Audit events"
            subtitle="Filter by subject and time window. Newest events appear first."
          >
            <form
              className={styles.filters}
              onSubmit={(ev) => {
                ev.preventDefault();
                applyFilters();
              }}
            >
              <FormField
                label="Subject (sub)"
                value={draftSub}
                onChange={(e) => setDraftSub(e.target.value)}
                placeholder="auth0|…"
              />
              <FormField
                label="From"
                type="datetime-local"
                value={draftFrom}
                onChange={(e) => setDraftFrom(e.target.value)}
              />
              <FormField
                label="To"
                type="datetime-local"
                value={draftTo}
                onChange={(e) => setDraftTo(e.target.value)}
              />
              <Button type="submit" variant="secondary">
                Apply filters
              </Button>
            </form>

            {loading ? (
              <p role="status" className={styles.loading}>
                Loading audit log…
              </p>
            ) : (
              <>
                <Table
                  caption="Authentication audit events"
                  hideCaption={false}
                  columns={columns}
                  rows={entries}
                  rowKey={(e) => e.id}
                  emptyMessage="No audit events to show. Login and logout events are not stored in ShopLink."
                />
                {nextCursor !== null ? (
                  <div className={styles.pagination}>
                    <Button variant="secondary" onClick={loadMore} disabled={loadingMore}>
                      {loadingMore ? 'Loading…' : 'Load more'}
                    </Button>
                  </div>
                ) : null}
              </>
            )}
          </Card>
        </>
      )}
    </AppShell>
  );
}

// The variant `payload` fields for a forensic view (D-4). Rendered as a
// disclosure so the table stays scannable; empty payloads show a dash.
function PayloadDetails({ payload }: { payload: Record<string, unknown> }) {
  // Defensive: the client guard rejects a drifted (non-object) payload before we
  // reach here — never call Object.keys on a null.
  const keys = payload != null && typeof payload === 'object' ? Object.keys(payload) : [];
  if (keys.length === 0) return <span className={styles.muted}>—</span>;
  return (
    <details className={styles.details}>
      <summary>{`${keys.length} field${keys.length === 1 ? '' : 's'}`}</summary>
      <dl className={styles.payload}>
        {keys.map((key) => (
          <div key={key} className={styles.payloadRow}>
            <dt className={styles.payloadKey}>{key}</dt>
            <dd className={styles.payloadValue}>{formatValue(payload[key])}</dd>
          </div>
        ))}
      </dl>
    </details>
  );
}

function formatValue(value: unknown): string {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'string') return value;
  return JSON.stringify(value);
}

/**
 * Normalise a zone-less `datetime-local` wall-clock string (e.g.
 * `2026-07-01T00:00`) to a UTC ISO instant. Empty stays empty (no bound). The
 * browser parses the zone-less value in the user's LOCAL zone, so `toISOString`
 * yields the correct UTC instant for the admin's intended wall-clock time.
 */
function toUtcInstant(localWallTime: string): string {
  if (localWallTime === '') return '';
  const d = new Date(localWallTime);
  return Number.isNaN(d.getTime()) ? '' : d.toISOString();
}
